import type { ProjectShareContent } from '../types/projects';
import { agreementPdfCopy } from '../lib/clientCopy';
import { formatClientCurrency, formatClientDate, type ClientLocale } from '../lib/clientLocale';
import { studioAddressLines } from '../lib/studioBranding';
import { APP_MARK } from '../lib/favicon';
import type {
  ProjectShareBranding,
  ProjectShareLineItem,
  ProjectShareQuote,
  ProjectShareStatus,
} from '../lib/projectShare';

type BriefClient = {
  name?: string;
  contact?: string;
  email?: string;
  phone?: string;
  address?: string | null;
  locale?: string | null;
};

type Props = {
  projectName: string;
  description?: string | null;
  client?: BriefClient | null;
  brief?: Partial<ProjectShareContent> | null;
  lineItems?: ProjectShareLineItem[];
  quote?: ProjectShareQuote | null;
  contractText?: string;
  branding: ProjectShareBranding;
  shareStatus?: ProjectShareStatus | string;
  approved?: { at?: string | null; name?: string | null; email?: string | null } | null;
  locale: ClientLocale;
};

export function ProjectBriefDocument({
  projectName,
  description,
  client,
  brief,
  lineItems = [],
  quote,
  contractText,
  branding,
  shareStatus,
  approved,
  locale,
}: Props) {
  const copy = agreementPdfCopy(locale);
  const money = (value: number | null | undefined) =>
    formatClientCurrency(value, locale, quote?.currency || branding.currency || 'USD');
  const primary = branding.primaryColor || '#1b7a74';
  const title = String(brief?.title || projectName || '').trim();
  const summary = String(brief?.summary || description || '').trim();
  const value = String(brief?.value || '').trim();
  const highlights = Array.isArray(brief?.highlights)
    ? brief.highlights.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const gallery = Array.isArray(brief?.gallery)
    ? brief.gallery.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const resources = Array.isArray(brief?.resources)
    ? brief.resources.filter((item) => item?.label && item?.url)
    : [];
  const fromLines = [
    branding.legalName || branding.studioName,
    branding.contactEmail,
    branding.phone,
    ...studioAddressLines(branding),
    branding.taxId ? `${copy.taxId}: ${branding.taxId}` : '',
  ].filter(Boolean);
  const toLines = [
    client?.name || (locale === 'es' ? 'Cliente' : 'Client'),
    client?.contact ? `${copy.attn}: ${client.contact}` : '',
    client?.email || '',
    client?.phone || '',
    ...(client?.address || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean),
  ].filter(Boolean);
  const detailLines = [
    `${copy.project}: ${projectName}`,
    `${copy.status}: ${copy.statusLabel(shareStatus || '')}`,
    approved?.name ? `${copy.approvedBy}: ${approved.name}` : '',
    approved?.at ? `${copy.approvedAt}: ${formatClientDate(approved.at, locale)}` : '',
  ].filter(Boolean);
  const sowRows = lineItems.length
    ? lineItems
    : quote
      ? [
          {
            name: projectName,
            description: '',
            quantity: 1,
            unit_price: quote.amount,
            tax_rate: null,
            total: quote.amount,
          },
        ]
      : [];
  const total = sowRows.reduce((sum, item) => sum + Number(item.total || 0), 0);

  return (
    <article className="overflow-hidden bg-[#f4f1ea] text-[#111111] shadow-sm">
      <header className="flex items-center justify-between gap-6 bg-[#0B0F14] px-8 py-6">
        <div className="flex min-w-0 items-center gap-3">
          <img
            src={branding.logoUrl || APP_MARK}
            alt={branding.studioName}
            className="h-11 max-w-[168px] object-contain"
          />
          {!branding.logoUrl ? (
            <span className="truncate text-base font-semibold text-[#f4f1ea]">{branding.studioName}</span>
          ) : null}
        </div>
        <p className="shrink-0 text-[22px] font-bold tracking-wide" style={{ color: primary }}>
          {copy.title}
        </p>
      </header>

      <div className="grid gap-8 px-8 py-8 sm:grid-cols-3">
        {[
          { title: copy.from, lines: fromLines },
          { title: copy.to, lines: toLines },
          { title: copy.details, lines: detailLines },
        ].map((column) => (
          <div key={column.title}>
            <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{column.title}</p>
            <div className="mt-2 space-y-1 text-sm leading-relaxed">
              {column.lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(title || summary || value || highlights.length || gallery.length || resources.length || brief?.cta) && (
        <section className="space-y-5 px-8 pb-8">
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{copy.scope}</p>
          {brief?.heroImage ? (
            <img src={brief.heroImage} alt="" className="max-h-64 w-full object-cover" />
          ) : null}
          {title ? <h2 className="text-xl font-semibold tracking-tight">{title}</h2> : null}
          {summary ? <p className="text-sm leading-relaxed text-neutral-800">{summary}</p> : null}
          {value ? <p className="text-sm leading-relaxed text-neutral-800">{value}</p> : null}
          {highlights.length ? (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{copy.highlights}</p>
              <ul className="mt-2 space-y-1 text-sm">
                {highlights.map((item) => (
                  <li key={item}>• {item}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {gallery.length ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {gallery.map((url) => (
                <img key={url} src={url} alt="" className="aspect-square w-full object-cover" />
              ))}
            </div>
          ) : null}
          {resources.length ? (
            <div>
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{copy.resources}</p>
              <ul className="mt-2 space-y-1 text-sm">
                {resources.map((resource) => (
                  <li key={`${resource.label}-${resource.url}`}>
                    <a href={resource.url} className="underline" style={{ color: primary }} target="_blank" rel="noreferrer">
                      {resource.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {brief?.cta ? <p className="text-sm font-medium" style={{ color: primary }}>{brief.cta}</p> : null}
        </section>
      )}

      <section className="px-8 pb-8">
        <p className="mb-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{copy.sow}</p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr style={{ background: mixWash(primary) }}>
              <th className="px-3 py-2 text-left font-semibold">{copy.item}</th>
              <th className="px-3 py-2 text-right font-semibold">{copy.quantity}</th>
              <th className="px-3 py-2 text-right font-semibold">{copy.unitPrice}</th>
              <th className="px-3 py-2 text-right font-semibold">{copy.total}</th>
            </tr>
          </thead>
          <tbody>
            {sowRows.length ? (
              sowRows.map((item, index) => (
                <tr key={`${item.name}-${index}`} className="border-b border-neutral-200">
                  <td className="px-3 py-2">
                    <div>{item.name}</div>
                    {item.description ? <div className="text-xs text-neutral-500">{item.description}</div> : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{item.quantity}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(item.unit_price)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(item.total)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-3 py-3 text-neutral-500" colSpan={4}>
                  —
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end gap-8 text-sm">
          <span className="font-semibold">{copy.total}</span>
          <span className="tabular-nums font-semibold">{money(total)}</span>
        </div>
      </section>

      {contractText?.trim() ? (
        <section className="px-8 pb-8">
          <p className="mb-3 text-[9px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{copy.contract}</p>
          <div className="whitespace-pre-wrap text-sm leading-relaxed text-neutral-800">{contractText}</div>
        </section>
      ) : null}

      <footer className="flex items-center gap-2 border-t border-neutral-200 px-8 py-4 text-[7px] uppercase tracking-[0.12em] text-neutral-500">
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: primary }} />
        {copy.footer.replace('{{email}}', branding.contactEmail)}
      </footer>
    </article>
  );
}

function mixWash(hex: string) {
  const raw = hex.replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((char) => char + char).join('') : raw;
  const n = Number.parseInt(full, 16);
  if (Number.isNaN(n)) return '#d7ebe9';
  const r = Math.round(((n >> 16) & 255) + (255 - ((n >> 16) & 255)) * 0.82);
  const g = Math.round(((n >> 8) & 255) + (255 - ((n >> 8) & 255)) * 0.82);
  const b = Math.round((n & 255) + (255 - (n & 255)) * 0.82);
  return `rgb(${r}, ${g}, ${b})`;
}
